import React from 'react';
import { motion } from 'framer-motion';

/**
 * OnboardingCard - Premium card component with subtle animated border glow
 * 
 * Features:
 * - Glassmorphism effect
 * - Subtle animated perimeter glow in brand emerald
 * - Rounded corners preserved
 * - Premium fintech feel
 */
const OnboardingCard = ({ 
  children, 
  className = '', 
  showGlow = true,
  glowIntensity = 'normal' // 'subtle', 'normal', 'strong'
}) => {
  const glowOpacity = {
    subtle: 0.15,
    normal: 0.25,
    strong: 0.4,
  }[glowIntensity];

  return (
    <div className={`relative ${className}`}>
      {/* Animated Glow Border */}
      {showGlow && (
        <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
          {/* Base glow */}
          <div 
            className="absolute inset-0 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, rgba(16, 185, 129, ${glowOpacity}), rgba(5, 150, 105, ${glowOpacity * 0.5}), rgba(16, 185, 129, ${glowOpacity}))`,
              filter: 'blur(1px)',
            }}
          />
          
          {/* Animated traveling highlight */}
          <motion.div
            className="absolute inset-0"
            style={{
              background: `conic-gradient(from 0deg, transparent, rgba(16, 185, 129, ${glowOpacity * 1.5}), transparent, transparent)`,
            }}
            animate={{
              rotate: [0, 360],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
          
          {/* Subtle pulse overlay */}
          <motion.div
            className="absolute inset-0 rounded-2xl"
            style={{
              boxShadow: `0 0 30px rgba(16, 185, 129, ${glowOpacity * 0.5})`,
            }}
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>
      )}
      
      {/* Card Content */}
      <div 
        className="relative bg-stone-800/90 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden"
        style={{
          boxShadow: showGlow 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(16, 185, 129, 0.1)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * OnboardingButton - Primary CTA button with premium styling
 */
export const OnboardingButton = ({ 
  children, 
  onClick, 
  disabled = false,
  variant = 'primary', // 'primary', 'secondary', 'ghost'
  size = 'lg', // 'sm', 'md', 'lg'
  className = '',
  loading = false,
}) => {
  const baseStyles = "relative font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2";
  
  const variants = {
    primary: `
      bg-gradient-to-r from-emerald-500 to-emerald-600 
      hover:from-emerald-400 hover:to-emerald-500 
      text-white shadow-lg shadow-emerald-500/25
      hover:shadow-emerald-500/40 hover:scale-[1.02]
      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
    `,
    secondary: `
      bg-slate-700/50 hover:bg-slate-700
      text-slate-200 border border-slate-600
      hover:border-slate-500
    `,
    ghost: `
      bg-transparent hover:bg-slate-700/30
      text-slate-300 hover:text-white
    `,
  };
  
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading ? (
        <>
          <motion.div
            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          Processing...
        </>
      ) : children}
    </button>
  );
};

/**
 * OnboardingInput - Styled input field
 */
export const OnboardingInput = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  error,
  className = '',
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-300">
          {label}
          {required && <span className="text-emerald-400 ml-1">*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`
          w-full px-4 py-3 rounded-xl
          bg-slate-700/50 border border-slate-600
          text-white placeholder-slate-400
          focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500
          transition-all duration-200
          ${error ? 'border-red-500 focus:ring-red-500/50' : ''}
        `}
      />
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};

/**
 * OnboardingHeading - Consistent heading styles
 */
export const OnboardingHeading = ({ title, subtitle, className = '' }) => {
  return (
    <div className={`text-center space-y-3 ${className}`}>
      <h1 className="text-3xl sm:text-4xl font-bold text-white">
        {title}
      </h1>
      {subtitle && (
        <p className="text-lg text-slate-400 max-w-md mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default OnboardingCard;
